import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Clock, MessageCircle } from "lucide-react";
import { getGuide, GUIDES } from "../data/guides";
import { waLink } from "../lib/store";
import Seo, { breadcrumbSchema, articleSchema } from "../components/Seo";
import { Button } from "../components/ui/button";

export default function EducationGuide() {
  const { slug } = useParams();
  const guide = getGuide(slug);

  if (!guide) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="font-heading text-3xl text-[#1a1713]">Guide Not Found</h1>
        <Link to="/education"><Button className="mt-6 rounded-none bg-[#1a1713] text-[#f3ead2] hover:bg-[#3a2f1d]">All Guides</Button></Link>
      </div>
    );
  }

  const others = GUIDES.filter((g) => g.slug !== slug).slice(0, 3);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-14 pb-4">
      <Seo
        title={guide.title}
        description={guide.summary}
        path={`/education/${guide.slug}`}
        type="article"
        jsonLd={[
          articleSchema(guide),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Education", path: "/education" },
            { name: guide.title, path: `/education/${guide.slug}` },
          ]),
        ]}
      />
      <p className="text-xs tracking-[0.18em] uppercase text-[#8f8571]">
        <Link to="/" className="hover:text-[#a8842c] transition-colors">Home</Link> /{" "}
        <Link to="/education" className="hover:text-[#a8842c] transition-colors">Education</Link>
      </p>
      <h1 className="font-heading text-4xl sm:text-5xl text-[#1a1713] leading-tight mt-4">{guide.title}</h1>
      <p className="flex items-center gap-2 text-xs text-[#8f8571] mt-4"><Clock size={13} /> {guide.minutes} min read · By the Regal Jewels team</p>
      <p className="text-[#5c5240] leading-relaxed mt-6 text-lg">{guide.summary}</p>

      <div className="mt-10 space-y-10">
        {guide.sections.map((s) => (
          <section key={s.h}>
            <h2 className="font-heading text-2xl sm:text-3xl text-[#1a1713]">{s.h}</h2>
            <p className="text-[#5c5240] leading-relaxed mt-4">{s.p}</p>
          </section>
        ))}
      </div>

      {/* CTA */}
      <div className="bg-[#f4efe5] p-8 mt-14">
        <h3 className="font-heading text-2xl text-[#1a1713]">Still have questions?</h3>
        <p className="text-sm text-[#5c5240] mt-2">Our jewelers answer questions every day — in store on Devon Avenue, or on WhatsApp.</p>
        <div className="flex flex-wrap gap-4 mt-6">
          {guide.related.map((r) => (
            <Link key={r.to} to={r.to} className="editorial-link">{r.label} <ArrowRight size={11} /></Link>
          ))}
          <a href={waLink(`Hello Regal Jewels! I just read your guide "${guide.title}" and have a question.`)} target="_blank" rel="noopener noreferrer" className="editorial-link">
            <MessageCircle size={12} /> Ask on WhatsApp
          </a>
        </div>
      </div>

      {/* More guides */}
      <div className="border-t border-[#eae2d0] mt-14 pt-10">
        <p className="section-eyebrow">Keep Learning</p>
        <div className="space-y-3">
          {others.map((g) => (
            <Link key={g.slug} to={`/education/${g.slug}`} className="block font-heading text-xl text-[#1a1713] hover:text-[#a8842c] transition-colors duration-200">
              {g.title} →
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
